import React, { useState, useEffect, useMemo } from "react";
import { PiDotsSixVertical } from "react-icons/pi";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaPlus } from "react-icons/fa6";
import { Link } from "react-router-dom";
import ModalForm from "./Modal";
import _ from "lodash";
import { URLS } from "../../../Services/Urls";
import { API } from "../../../Services/axios";
import { useSelector, useDispatch } from "react-redux";
import {
  removeProduct,
  removeFromSection,
} from "../../../Redux/Store/Slices/sectionSlice";
import AddContentModal from "./AddContentModal";
import { setActiveSection } from "../../../Redux/Store/Slices/sectionSlice";
import {
  updateSection,
  reUpdateSection,
  setProjectData,
} from "../../../Redux/Store/Slices/projectReducer";
import NestedSection from "./NestedSection";
import ParentSectionCard from "./ParentSectionCard";
import InputField from "./InputField";
import ChoiceProductDisplay from "./ChoiceProductDisplay";
import "./nested-sections.css";
import {
  buildProductImageUrl,
  getFallbackImageUrl,
  getProductImageUrl,
} from "../../../utils/productImage";

const buildSectionLookup = (sectionsList = [], lookup = {}) => {
  sectionsList.forEach((section) => {
    if (!section || !section.id) {
      return;
    }
    lookup[section.id] = section;

    if (Array.isArray(section.nested_sections) && section.nested_sections.length) {
      buildSectionLookup(section.nested_sections, lookup);
    }

    if (Array.isArray(section.sections) && section.sections.length) {
      buildSectionLookup(section.sections, lookup);
    }
  });
  return lookup;
};

const MainBody = ({ sections, sectionId }) => {
  const dispatch = useDispatch();
  const [isActive, setIsActive] = useState(
    sections.reduce((acc, section) => ({ ...acc, [section.id]: true }), {})
  );
  const [showSelectedProducts, setShowSelectedProducts] = useState(
    sections.reduce((acc, section) => ({ ...acc, [section.id]: [] }), {})
  );
  const [selectedChoices, setSelectedChoices] = useState({});
  const [choiceProductRefs, setChoiceProductRefs] = useState({}); // Store linked_product for each choice (product ID)

  const selectedProducts = useSelector(
    (state) => state.products.selectedProducts
  );
  const { active_section } = useSelector((state) => state.section);
  const sectionProducts = useSelector((state) => state.project.active_section.products);
  const project = useSelector((state) => state.project);
  // const selectedProducts = useSelector((state) => state.products.sections[sectionId] || []);

  const sectionLookup = useMemo(() => buildSectionLookup(sections), [sections]);

  const firstSectionByParentId = useMemo(() => {
    const map = {};
    sections.forEach((section) => {
      const parentId = section.parent_section?.id;
      if (parentId && !map[parentId]) {
        map[parentId] = section.id;
      }
    });
    return map;
  }, [sections]);

  // Helper function to check if a section should be displayed based on conditions
  const shouldDisplaySection = (section, allSections) => {
    if (!section.condition_from_prev_step) {
      return true;
    }

    const condition = section.condition_from_prev_step;
    
    // Handle string-based conditions (e.g., "TYPE_LAMES_BOIS" from BARDAGE JSON)
    if (typeof condition === 'string') {
      // Find the parent section that might contain the choice with this code
      let parentSection = null;
      
      // First, try to find by parent_section relationship
      if (section.parent_section) {
        const parentId = typeof section.parent_section === 'string' 
          ? section.parent_section 
          : section.parent_section.id;
        parentSection = allSections.find((s) => s.id === parentId);
      }
      
      // If not found, search through all sections to find one with a matching choice code
      if (!parentSection) {
        parentSection = allSections.find((s) => {
          const choiceItems = 
            (Array.isArray(s.choice_items) && s.choice_items.length > 0
              ? s.choice_items
              : Array.isArray(s.choices)
              ? s.choices
              : []) || [];
          return choiceItems.some((choice) => choice.code === condition);
        });
      }
      
      if (!parentSection) {
        // If no parent section found, show the section (might be a top-level section)
        return true;
      }
      
      // Check if a choice with the matching code is selected
      const parentSectionId = parentSection.id;
      const selectedChoice = selectedChoices[parentSectionId];
      
      if (selectedChoice) {
        // Check if the selected choice's code matches the condition
        if (selectedChoice.code === condition) {
          // Also verify this section is in the choice's children array if it exists
          const choiceItems = 
            (Array.isArray(parentSection.choice_items) && parentSection.choice_items.length > 0
              ? parentSection.choice_items
              : Array.isArray(parentSection.choices)
              ? parentSection.choices
              : []) || [];
          
          const matchingChoice = choiceItems.find((c) => c.code === condition || c.id === selectedChoice.id);
          if (matchingChoice && Array.isArray(matchingChoice.children)) {
            return matchingChoice.children.includes(section.id);
          }
          
          return true;
        }
      }
      
      // If no matching choice is selected, hide the section
      return false;
    }
    
    // Handle object-based conditions (existing logic)
    if (typeof condition === 'object') {
      let previousSectionId = condition.section_id || condition.section;
      let previousSection = allSections.find((s) => s.id === previousSectionId);

      // If section_id is not found, try to find by parent_section relationship
      if (!previousSection && section.parent_section) {
        const parentId = typeof section.parent_section === 'string' 
          ? section.parent_section 
          : section.parent_section.id;
        previousSection = allSections.find((s) => s.id === parentId);
        previousSectionId = parentId;
      }

      if (!previousSection) {
        // If no previous section found, show the section (might be a top-level section)
        return true;
      }

      // Check if condition is based on choice selection
      if (condition.choice_id) {
        const selectedChoice = selectedChoices[previousSectionId];
        const isMatch = selectedChoice?.id === condition.choice_id;
        
        // Also check if this section is in the choice's children array
        const choiceItems = 
          (Array.isArray(previousSection.choice_items) && previousSection.choice_items.length > 0
            ? previousSection.choice_items
            : Array.isArray(previousSection.choices)
            ? previousSection.choices
            : []) || [];
        
        const matchingChoice = choiceItems.find((c) => c.id === condition.choice_id);
        if (matchingChoice && Array.isArray(matchingChoice.children)) {
          const isChildOfChoice = matchingChoice.children.includes(section.id);
          return isMatch && isChildOfChoice;
        }
        
        return isMatch;
      }

      // Check if condition is based on input value
      if (condition.input_id && condition.expected_value !== undefined) {
        const input = previousSection.inputs?.find((i) => i.id === condition.input_id);
        if (input) {
          return input.value === condition.expected_value || 
                 String(input.value) === String(condition.expected_value);
        }
      }

      // If condition exists but doesn't match any criteria, hide the section
      return false;
    }

    // If condition is neither string nor object, show the section
    return true;
  };

  const handleInputChange = (inputId, newValue) => {
    // Update the section in Redux with the new input value
    const section = sections.find((s) => 
      s.inputs?.some((input) => input.id === inputId)
    );
    
    if (section) {
      const updatedInputs = section.inputs.map((input) =>
        input.id === inputId ? { ...input, value: newValue } : input
      );
      
      const updatedSection = {
        ...section,
        inputs: updatedInputs,
      };
      
      dispatch(updateSection(updatedSection));
    }
  };

  useEffect(() => {
    console.log("Selected Products:", selectedProducts);
  }, [selectedProducts]); 

  useEffect(() => {
    setSelectedChoices((prevChoices) => {
      const updatedChoices = { ...prevChoices };
      sections.forEach((section) => {
        if (!(section.id in updatedChoices)) {
          updatedChoices[section.id] = null;
        }
      });
      return updatedChoices;
    });

    // Initialize choice linked_products from sections (including nested sections)
    const initialChoiceProductRefs = {};
    
    const extractChoicesFromSection = (section) => {
      const choiceItems =
        (Array.isArray(section.choice_items) && section.choice_items.length > 0
          ? section.choice_items
          : Array.isArray(section.choices)
          ? section.choices
          : []) || [];
      
      choiceItems.forEach((choice) => {
        // Check both product_ref (main product) and linked_product (additional product)
        const productId = choice.product_ref || choice.linked_product;
        if (productId) {
          initialChoiceProductRefs[choice.id] = productId;
          console.log(`Initialisé produit pour choix ${choice.id}: ${productId} (product_ref: ${choice.product_ref}, linked_product: ${choice.linked_product})`);
        }
      });
      
      // Also check nested sections
      if (Array.isArray(section.nested_sections)) {
        section.nested_sections.forEach(nestedSection => {
          extractChoicesFromSection(nestedSection);
        });
      }
      
      // Also check sections within nested sections
      if (Array.isArray(section.sections)) {
        section.sections.forEach(subSection => {
          extractChoicesFromSection(subSection);
        });
      }
    };
    
    sections.forEach((section) => {
      extractChoicesFromSection(section);
    });
    
    console.log('Product refs initialisés:', initialChoiceProductRefs);
    setChoiceProductRefs((prev) => ({ ...prev, ...initialChoiceProductRefs }));
  }, [sections]);

  const toggleSection = (sectionId) => {
    setIsActive((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleAddProductModal = (event, section) => {
    event.preventDefault();
    console.log("section:", section);

    const normalizedProducts = Array.isArray(section.products) ? [...section.products] : [];

    dispatch(
      setActiveSection({
        ...section,
        products: normalizedProducts,
        contents: Array.isArray(section.contents) ? [...section.contents] : [],
        isNestedSection: false,
        initialProductIds: normalizedProducts
          .map((product) => product.product?.id || product.id)
          .filter(Boolean),
      })
    );
  };

  const handleAddProductToChoice = (event, choice, section) => {
    event.preventDefault();
    event.stopPropagation(); // Prevent choice selection when clicking the button
    
    console.log("Adding product to choice:", choice, "in section:", section);

    // Get existing product_ref or linked_product for this choice
    const existingProductRef = choiceProductRefs[choice.id] || choice.product_ref || choice.linked_product || null;

    // Create a temporary section-like object for the choice to use with Modal
    // For choices, we only allow one product, so we'll use a single product structure
    const choiceAsSection = {
      id: choice.id,
      name: choice.label || choice.code || "Choice",
      products: existingProductRef ? [{
        id: _.uniqueId(),
        product: { id: existingProductRef },
        product_type: "LAMBOURDE"
      }] : [],
      contents: [],
      isNestedSection: false,
      isChoice: true, // Flag to indicate this is a choice, not a section
      parentSectionId: section.id,
      initialProductIds: existingProductRef ? [existingProductRef] : [],
      maxProducts: 1, // Only one product allowed for choices
    };

    dispatch(setActiveSection(choiceAsSection));
  };

  const handleShowSelectedProducts = (selectedProducts, sectionId) => {
    setShowSelectedProducts((prevSelectedProducts) => ({
      ...prevSelectedProducts,
      [sectionId]: selectedProducts,
    }));
    
    // Afficher un toast de confirmation
    if (selectedProducts && selectedProducts.length > 0) {
      console.log(`${selectedProducts.length} produit(s) ajouté(s) à la section ${sectionId}`);
      // Note: Le toast sera géré dans Modal.jsx après l'appel API
    }
  };

  const handleShowSelectedProductsForChoice = async (choiceId, selectedProducts) => {
    // For choices, we only take the first product and store its ID in product_ref
    const productToStore = Array.isArray(selectedProducts) && selectedProducts.length > 0 
      ? selectedProducts[0] 
      : null;
    
    const productId = productToStore?.product?.id || null;

    console.log(`Mise à jour du choix ${choiceId} avec product_ref: ${productId}`);

    // Update local state
    setChoiceProductRefs((prev) => {
      const updated = {
        ...prev,
        [choiceId]: productId,
      };
      console.log('choiceProductRefs mis à jour:', updated);
      return updated;
    });

    // Update choice via API by updating the parent section
    if (productId) {
      // Find the section containing this choice
      const section = sections.find((s) => {
        const items = s.choice_items || s.choices || [];
        return items.some((c) => c.id === choiceId);
      });
      
      if (!section) {
        const { toast } = await import("react-toastify");
        toast.error("Section parente non trouvée");
        return;
      }
      
      try {
        // Update the choice in the section's choice_items
        // Store product only in product_ref
        const updatedChoiceItems = (section.choice_items || section.choices || []).map((c) =>
          c.id === choiceId ? { ...c, product_ref: productId } : c
        );
        
        // Update Redux state first
        const updatedSection = {
          ...section,
          choice_items: updatedChoiceItems,
          choices: updatedChoiceItems,
        };
        
        dispatch(updateSection(updatedSection));
        
        // Update the entire project via PUT (as done in structure.jsx)
        if (project && project.id) {
          const updatedProject = {
            ...project,
            steps: project.steps.map(step => ({
              ...step,
              sections: step.sections.map(s => {
                if (s.id === section.id) {
                  return {
                    ...s,
                    choice_items: updatedChoiceItems,
                    choices: updatedChoiceItems,
                  };
                }
                return s;
              })
            }))
          };
          
          console.log('Projet à sauvegarder avec choix modifié (body.jsx):', JSON.stringify(updatedProject, null, 2));
          
          // Update Redux with the full project to keep it in sync
          // The user will save the project manually using the "Save" button
          dispatch(setProjectData(updatedProject));
          
          // Note: We don't save immediately to avoid sync issues
          // The user should click "Save" to persist all changes
        }

        // Note: Le toast de succès est déjà affiché dans Modal.jsx, pas besoin de le refaire ici
      } catch (error) {
        console.error("Erreur lors de la mise à jour du projet avec le choix via l'API:", error);
        const { toast } = await import("react-toastify");
        toast.error("Erreur lors de l'ajout du produit au choix.");
      }
    }
  };

  const handleRemoveProductFromChoice = async (choiceId) => {
    // Find the section containing this choice
    const section = sections.find((s) => {
      const items = s.choice_items || s.choices || [];
      return items.some((c) => c.id === choiceId);
    });
    
    if (!section) {
      const { toast } = await import("react-toastify");
      toast.error("Section parente non trouvée");
      return;
    }
    
    // Update local state
    setChoiceProductRefs((prev) => {
      const updated = { ...prev };
      delete updated[choiceId];
      return updated;
    });

    // Update choice via API by updating the parent section
    try {
      // Update the choice in the section's choice_items
      // Remove product from both product_ref and linked_product
      const updatedChoiceItems = (section.choice_items || section.choices || []).map((c) =>
        c.id === choiceId ? { ...c, product_ref: null, linked_product: null } : c
      );
      
      // Update Redux state first
      const updatedSection = {
        ...section,
        choice_items: updatedChoiceItems,
        choices: updatedChoiceItems,
      };
      
      dispatch(updateSection(updatedSection));
      
      // Update the entire project via PUT (as done in structure.jsx)
      if (project && project.id) {
        const updatedProject = {
          ...project,
          steps: project.steps.map(step => ({
            ...step,
            sections: step.sections.map(s => {
              if (s.id === section.id) {
                return {
                  ...s,
                  choice_items: updatedChoiceItems,
                  choices: updatedChoiceItems,
                };
              }
              return s;
            })
          }))
        };
        
        console.log('Projet à sauvegarder avec choix supprimé:', JSON.stringify(updatedProject, null, 2));
        
        // Update Redux with the full project to keep it in sync
        // The user will save the project manually using the "Save" button
        dispatch(setProjectData(updatedProject));
        
        // Note: We don't save immediately to avoid sync issues
        // The user should click "Save" to persist all changes
      }

      const { toast } = await import("react-toastify");
      toast.success("Produit supprimé du choix avec succès !");
    } catch (error) {
      console.error("Erreur lors de la suppression du produit du choix via l'API:", error);
      const { toast } = await import("react-toastify");
      toast.error("Erreur lors de la suppression du produit.");
    }
  };

  const handleChoiceSelect = (sectionId, choiceItem) => {
    setSelectedChoices((prevChoices) => {
      const currentChoice = prevChoices[sectionId];
      const isSameChoice = currentChoice && currentChoice.id === choiceItem.id;
      const updatedChoices = {
        ...prevChoices,
        [sectionId]: isSameChoice ? null : choiceItem,
      };

      console.log(
        `Choice item ${choiceItem.label} (code: ${choiceItem.code || 'N/A'}) ${
          isSameChoice ? "deselectionne" : "selectionne"
        } pour la section ${sectionId}`
      );

      // If choice has children, we need to trigger a re-render to show conditional sections
      if (choiceItem.children && choiceItem.children.length > 0) {
        console.log(`Choice ${choiceItem.label} has ${choiceItem.children.length} child section(s)`);
      }

      return updatedChoices;
    });
  };

  // const handleRemoveProduct = (product) => {
  //   dispatch(removeProduct(product));
  //   // dispatch(removeProduct({ sectionId: sectionId, product: product }));
  //   setShowSelectedProducts((prevSelectedProducts) => ({
  //     ...prevSelectedProducts,
  //     [sectionId]: prevSelectedProducts[sectionId]?.filter((p) => p.products.id !== product.products.id),
  //   }));
  //   console.log("sectionId :", sectionId)
  // };

  const handleRemoveProduct = async (section, product) => {
    if (!section) {
      return;
    }

    const entryId = product?.id; // id du content_product (clé primaire à supprimer)
    const productId = product?.product?.id; // id produit catalogue

    if (!entryId) {
      console.warn("Impossible de supprimer le produit : id du content_product manquant");
      const { toast } = await import("react-toastify");
      toast.error("Impossible de supprimer le produit : identifiant manquant");
      return;
    }

    // Appeler l'API pour supprimer le produit du backend
    try {
      console.log(`Suppression du content_product ${entryId} (section ${section.id})`);
      await API.delete(URLS.PROJECT.removeContentProductById(entryId));
      console.log("Produit supprimé avec succès du backend");
    } catch (error) {
      console.error("Erreur lors de la suppression du produit:", error);
      const { toast } = await import("react-toastify");
      toast.error("Erreur lors de la suppression du produit");
      return;
    }

    const currentProducts = Array.isArray(section.products)
      ? [...section.products]
      : [];

    const filteredProducts = currentProducts.filter((item) => item?.id !== entryId);

    if (filteredProducts.length === currentProducts.length) {
      console.warn("Aucun produit correspondant à retirer.");
      return;
    }

    const normalizedProducts = Array.isArray(section.products)
      ? [...section.products]
      : [];
    const normalizedContents = Array.isArray(section.contents)
      ? [...section.contents]
      : [];

    const initialProductIds = normalizedProducts
      .map((prod) => prod.product?.id || prod.id)
      .filter(Boolean);

    dispatch(
      setActiveSection({
        ...section,
        products: normalizedProducts,
        contents: normalizedContents,
        isNestedSection: false,
        initialProductIds,
      })
    );

    const productContent = {
      id: entryId || productId,
      product_type: product?.product_type || "",
      product: product?.product,
    };

    dispatch(removeFromSection(productContent));

    const updatedSection = {
      ...section,
      products: filteredProducts,
    };

    dispatch(updateSection(updatedSection));

    const { toast } = await import("react-toastify");
    toast.success("Produit supprimé avec succès");
  };

  return (
    <>
      <div className="border-circle mt-4 p-5 rounded row-cols-lg-12">
        <div>
          {sections
            .filter((section) => shouldDisplaySection(section, sections))
            .map((section, idx) => {
            const choiceItems =
              (Array.isArray(section.choice_items) && section.choice_items.length > 0
                ? section.choice_items
                : Array.isArray(section.choices)
                ? section.choices
                : []) || [];
            const parentId = section.parent_section?.id;
            const isPrimaryParentSection =
              parentId && firstSectionByParentId[parentId] === section.id;
            const hasInputs = Array.isArray(section.inputs) && section.inputs.length > 0;
            return (
              <div key={section.id}>
              <div
                className="border-circle rounded"
                id={`section-${section.id}`}
              >
                <div className="px-2 d-flex mb-3 border-btm">
                  <div className="d-flex me-auto">
                    <button
                      type="button"
                      className="p-col-2 fs-3 border-0 bg-white"
                    >
                      <PiDotsSixVertical />
                    </button>
                    <div>
                      <p className="section-text pt-3 ps-1">
                        Section {idx + 1}: {section.name}
                      </p>
                    </div>
                  </div>
                  <div className="p-2">
                    <button
                      type="button"
                      className="btn fs-italic"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModal"
                    >
                      <FaPlus /> Add Content
                    </button>
                    <AddContentModal />
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => toggleSection(section.id)}
                      type="button"
                      className="btn p-col-2 fs-3"
                    >
                      {isActive[section.id] ? (
                        <IoIosArrowUp />
                      ) : (
                        <IoIosArrowDown />
                      )}
                    </button>
                  </div>
                </div>
                {isActive[section.id] && (
                  <div>
                    {section.parent_section && isPrimaryParentSection && (
                      <ParentSectionCard
                        parentSection={section.parent_section}
                        sectionLookup={sectionLookup}
                        currentSectionId={section.id}
                      />
                    )}
                    {section.parent_section && parentId && !isPrimaryParentSection && (
                      <div className="parent-section-inline">
                        <small className="text-muted text-uppercase">
                          Section liée à :{" "}
                          <span className="fw-semibold">
                            {section.parent_section.name || section.parent_section.code}
                          </span>
                        </small>
                        <a
                          href={`#section-${firstSectionByParentId[parentId]}`}
                          className="parent-inline-link"
                        >
                          Voir la section principale
                        </a>
                      </div>
                    )}
                    {choiceItems.length > 0 && (
                      <div className="choice-items-wrapper">
                        <h6 className="ms-4 mb-3 text-muted d-flex align-items-center">
                          <i className="fas fa-check-square me-2" />
                          Choix disponibles ({choiceItems.length})
                        </h6>
                        <div className="choice-items-grid ms-3 me-3">
                          {choiceItems.map((choice) => {
                            const isSelected =
                              selectedChoices[section.id]?.id === choice.id;
                            const hasChildren =
                              Array.isArray(choice.children) &&
                              choice.children.length > 0;

                            const linkedProductImage =
                              getProductImageUrl(choice.linked_product) ||
                              getProductImageUrl(choice.product_ref);
                            const rawChoiceImage =
                              buildProductImageUrl(choice.image) ||
                              buildProductImageUrl(choice.profile_image);
                            const finalChoiceImage =
                              linkedProductImage ||
                              rawChoiceImage ||
                              getFallbackImageUrl();

                            // Get product_ref or linked_product from state or from choice object
                            // Priority: choiceProductRefs (local state) > choice.product_ref > choice.linked_product (from Redux/API)
                            const choiceProductRef = choiceProductRefs[choice.id] ?? choice.product_ref ?? choice.linked_product ?? null;
                            const hasProduct = choiceProductRef !== null && choiceProductRef !== undefined;
                            
                            // Debug log when product is found
                            if (hasProduct && !choiceProductRefs[choice.id]) {
                              console.log(`Choix ${choice.id} a un produit depuis choice.product_ref ou choice.linked_product:`, choice.product_ref || choice.linked_product);
                            }

                            return (
                              <div key={choice.id} className="choice-card-wrapper">
                                <button
                                  type="button"
                                  className={`choice-card ${
                                    isSelected ? "selected" : ""
                                  }`}
                                  onClick={() =>
                                    handleChoiceSelect(section.id, choice)
                                  }
                                >
                                  <div className="choice-card-header">
                                    <span className="choice-card-label">
                                      {choice.label}
                                    </span>
                                    {isSelected && (
                                      <span className="choice-card-badge">
                                        Selectionne
                                      </span>
                                    )}
                                  </div>
                                  {finalChoiceImage && (
                                    <div className="choice-card-image">
                                      <img
                                        src={finalChoiceImage}
                                        alt={choice.label}
                                      />
                                    </div>
                                  )}
                                  {choice.help_text && (
                                    <p className="choice-card-help">
                                      {choice.help_text}
                                    </p>
                                  )}
                                  <div className="choice-card-meta">
                                    <span>Code : {choice.code}</span>
                                    {choice.add_quantities && (
                                      <span className="choice-card-quantity">
                                        Quantite ajustable
                                      </span>
                                    )}
                                    {hasChildren && (
                                      <span className="choice-card-children">
                                        {choice.children.length} option(s)
                                        associee(s)
                                      </span>
                                    )}
                                    {hasProduct && (
                                      <span className="choice-card-products">
                                        1 produit associé
                                      </span>
                                    )}
                                  </div>
                                </button>
                                <div className="choice-card-actions mt-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    data-bs-toggle="modal"
                                    data-bs-target={`#exampleModal-${choice.id}`}
                                    onClick={(e) => handleAddProductToChoice(e, choice, section)}
                                  >
                                    <FaPlus /> {hasProduct ? "Modifier le produit" : "Ajouter un produit"}
                                  </button>
                                </div>
                                {hasProduct && (
                                  <ChoiceProductDisplay 
                                    choiceId={choice.id}
                                    productRef={choiceProductRef}
                                    onRemove={() => handleRemoveProductFromChoice(choice.id)}
                                  />
                                )}
                                <ModalForm
                                  sectionId={choice.id}
                                  choiceId={choice.id}
                                  parentSectionId={section.id}
                                  onShowSelectedProducts={(selectedProducts) =>
                                    handleShowSelectedProductsForChoice(choice.id, selectedProducts)
                                  }
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Display Input Fields */}
                    {hasInputs && (
                      <div className="inputs-wrapper ms-4 me-4 mb-4">
                        <h6 className="mb-3 text-muted d-flex align-items-center">
                          <i className="fas fa-keyboard me-2" />
                          Champs de saisie ({section.inputs.length})
                        </h6>
                        <div className="border rounded p-4 bg-light">
                          {section.inputs.map((input) => (
                            <InputField
                              key={input.id}
                              input={input}
                              sectionId={section.id}
                              onValueChange={handleInputChange}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <section>
                      <div className="row m-3">
                        {section.products?.map((product, productIndex) => {
                          const imageUrl =
                            getProductImageUrl(product.product) ||
                            getFallbackImageUrl();

                          return (
                            <div
                              key={product.product.id}
                              className="col-6 mb-4 mt-1"
                            >
                              <div
                                className=" border  d-flex justify-content-between  "
                                style={{ width: "100%" }}
                              >
                                <div className="d-flex pt-2 px-1">
                                  <div>
                                    <img
                                      className=""
                                      src={imageUrl}
                                      alt={product.product.name}
                                      style={{ width: "75px", height: "75px" }}
                                    />
                                  </div>
                                  <div className="design-text px-3">
                                    <span>{product.product.name}</span>
                                    <br />
                                    <p className="p-col mt-1">
                                      <span className="p-6-d me-4">
                                        Price: £{product.product.price}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                                <div className="position-relative me-3">
                                  <div className="text-top d-flex justify">
                                    <button
                                      type="button"
                                      className="btn bg-warning-subtle text-remove fw-bold fst-italic"
                                    >
                                      <Link
                                        className="p-col text-remove"
                                        onClick={() =>
                                          handleRemoveProduct(section, product)
                                        }
                                      >
                                        Remove
                                      </Link>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </section>
                    
                    {/* Afficher les sections imbriquées */}
                    {section.nested_sections && section.nested_sections.length > 0 && (
                      <div className="nested-sections-container mt-4">
                        <h6 className="ms-4 mb-3 text-muted">
                          <i className="fas fa-layer-group me-2"></i>
                          Sections Imbriquées ({section.nested_sections.length})
                        </h6>
                        {section.nested_sections.map((nestedSection) => (
                          <NestedSection 
                            key={nestedSection.id} 
                            nestedSection={nestedSection} 
                            level={0}
                          />
                        ))}
                      </div>
                    )}
                    
                    <hr className="ms-4" style={{ width: "95%" }} />
                    <div>
                      <div>
                        <button
                          type="button"
                          data-bs-toggle="modal"
                          data-bs-target={`#exampleModal-${section.id}`}
                          className="rounded border-dash mb-4 ms-4 pe-auto text-center fw -bold fst-italic py-2 bg-white p-6-d"
                          style={{ width: "95%" }}
                          onClick={(event) => handleAddProductModal(event, section)}
                        >
                          Add Product
                        </button>
                      </div>
                      <ModalForm
                        sectionId={section.id}
                        onShowSelectedProducts={(selectedProducts) =>
                          handleShowSelectedProducts(
                            selectedProducts,
                            section.id
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
              <hr className="my-4" />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default MainBody;

